import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import { useAuth } from "../../contexts/AuthContext";
import * as usersApi from "../../lib/mudoNetwork/usersApi";
import AvatarPickerModal from "../../components/Avatars/AvatarPickerModal";
import { persistAvatarForCurrentUser } from "../../lib/avatarPersistence";

function fireToast(message, tone = "info", ttl = 2200) {
  try {
    window.dispatchEvent(
      new CustomEvent("mudo:toast", { detail: { message, tone, ttl } })
    );
  } catch {
    // ignore
  }
}

export default function VisitorProfilePage() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth() || {};

  const derivedEmail = useMemo(() => user?.email || profile?.email || "", [user, profile]);

  const [displayName, setDisplayName] = useState("");
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);

  const avatarUrl = profile?.avatar_url || null;
  const avatarEmoji = profile?.avatar_emoji || "🙂";

  useEffect(() => {
    setDisplayName(profile?.display_name || "");
    setCountry(profile?.country || profile?.location || "");
  }, [profile]);

  const canSave = !!displayName.trim() && !!country.trim();

  const handleSave = async () => {
    if (!canSave) {
      fireToast("Please fill Display Name and Location.", "warn");
      return;
    }

    setSaving(true);
    try {
      const updates = {
        display_name: displayName.trim(),
        country: country.trim(),
      };

      const { error } = await usersApi.updateUserProfile(updates);
      if (error) throw error;

      await persistAvatarForCurrentUser({
        displayName: updates.display_name,
        country: updates.country,
      });

      try {
        localStorage?.setItem("mudo:userName", updates.display_name);
        localStorage?.setItem("mudo:userCountry", updates.country);
      } catch {
        // ignore storage failures
      }

      await refreshProfile?.();
      fireToast("Profile saved", "success");
    } catch (e) {
      console.error("[VisitorProfile] save failed", e);
      fireToast(e?.message || "Failed to save profile", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#0f0f10", color: "#fff" }}>
      <VisitorNavBar showBackButton backTo="/discover" />

      <div className="max-w-2xl mx-auto px-4 pt-24 pb-12">
        <div className="bg-[#1e1f22] border border-white/10 rounded-2xl shadow-2xl">
          <div className="p-6 border-b border-white/10">
            <h1 className="text-xl font-bold">Your Profile</h1>
            <p className="text-sm text-white/60 mt-1">
              Update your visitor profile details.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
                maxLength={50}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Email
              </label>
              <input
                type="email"
                value={derivedEmail}
                readOnly
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/10 text-white/60 placeholder-white/50 focus:outline-none"
              />
              <div className="text-xs text-white/40 mt-1">
                Email is managed by sign-in.
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Location
              </label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="City, Country"
                maxLength={100}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50"
              />
            </div>
          </div>

          <div className="p-6 border-t border-white/10 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate("/discover")}
              className="px-5 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold transition-colors"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || saving}
              className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <AvatarPickerModal
        open={avatarOpen}
        initialUrl={avatarUrl}
        onClose={() => setAvatarOpen(false)}
        onSave={async (nextUrl) => {
          setAvatarOpen(false);
          const res = await persistAvatarForCurrentUser({
            avatarUrl: nextUrl,
            avatarEmoji: nextUrl ? null : avatarEmoji,
          });
          if (!res?.ok) {
            console.warn("[VisitorProfile] persist avatar failed", res?.error);
          }
          await refreshProfile?.();
          try {
            window.dispatchEvent(new Event("mudo:profile-refresh"));
          } catch {}
        }}
      />
    </div>
  );
}
